import styles from './Landing.module.scss';
import ChartWrapper from 'components/chart-wrapper/ChartWrapper';
import ContentContainer from 'components/content-container/ContentContainer';
import RmCardsGroupSeries from 'components/rm-cards-group-series/RmCardsGroupSeries';
import ReactECharts from 'echarts-for-react';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    selectFiltersAllValues,
    selectFiltersDisplayMapping,
    selectMajorThreshold,
    selectMarketValue,
    selectMinorThreshold,
    selectRmValue,
    selectTeamValue,
    selectTimeslicerEndMonth,
    selectTimeslicerIsSameMonth,
    selectTimeslicerStartMonth,
    selectTopFiveEmergingMajorRmsData,
    selectTopFiveEmergingModerateRmsData,
    selectTopFiveRmsData,
    selectTopFiveSpikeData,
    selectTopFiveTeamsData,
    selectTopThreeMarketsData,
    selectZoneValue
} from 'selectors/selectors';
import {
    fetchLandingPageStats,
    fetchTopAndEmergingSpikes,
    fetchTopRanks
} from 'slices/LandingSlice';
import {
    FilterType,
    RMCardGroupDataType,
    RMCardGroupTooltipType
} from 'types/types';
import {
    generateRequestParamsForFilterApi,
    generateRequestParamsForLandingApi
} from 'utils/api';
import {
    landingTopThreeMarketOptions,
    landingTopThreeTeamsOptions,
    scoreDecompositionChartOptions
} from 'utils/charts';
import {
    calculateStartEndMonth,
    calculateTopValueForLandingGraphTooltip,
    generateUpperRightDateRangeContent,
    isTypeNumber
} from 'utils/common';
import { areAllFilterValuesNumberValues } from 'utils/filters';
import { fetchFilteredDropdownValues, updateFilter } from 'slices/FiltersSlice';
import { MARKET_ROUTE, TEAM_ROUTE } from 'constants/routes';
import { orderBy } from 'lodash';
import infoIcon from 'assets/images/info-icon.svg';
import darkInfoIcon from 'assets/images/dark-info-icon.svg';

const Landing = ({ navigateTo }) => {
    const dispatch = useDispatch();

    // Selectors
    const tsStart = useSelector(selectTimeslicerStartMonth);
    const tsEnd = useSelector(selectTimeslicerEndMonth);
    const isSameMonth = useSelector(selectTimeslicerIsSameMonth);
    const allValues = useSelector(selectFiltersAllValues);

    const dates = calculateStartEndMonth(tsStart, tsEnd, isSameMonth);
    const nonSpikeDates = calculateStartEndMonth(
        tsStart,
        tsEnd,
        isSameMonth,
        true
    );
    const { start, end } = { ...dates };
    const { start: nonSpikeStart, end: nonSpikeEnd } = { ...nonSpikeDates };

    const zoneValue = useSelector(selectZoneValue);
    const marketValue = useSelector(selectMarketValue);
    const teamValue = useSelector(selectTeamValue);
    const rmValue = useSelector(selectRmValue);
    const displayMappings = useSelector(selectFiltersDisplayMapping);
    const { market: marketList, team: teamList } = displayMappings;

    const topThreeMarkets = useSelector(selectTopThreeMarketsData);
    const topFiveTeams = useSelector(selectTopFiveTeamsData);

    const topFiveRms = useSelector(selectTopFiveRmsData);
    const topFiveRmsSpike = useSelector(selectTopFiveSpikeData);
    const topFiveEmergingModerateRms = useSelector(
        selectTopFiveEmergingModerateRmsData
    );
    const topFiveEmergingMajorRms = useSelector(
        selectTopFiveEmergingMajorRmsData
    );
    const minorThreshold = useSelector(selectMinorThreshold);
    const majorThreshold = useSelector(selectMajorThreshold);

    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipData, setTooltipData] = useState([]);
    const [chartsContainerHeight, setChartsContainerHeight] = useState(null);
    const [selectedDataIndex, setSelectedDataIndex] = useState(0);
    const [barValue, setBarValue] = useState(0);
    const [highestScore, setHighestScore] = useState(0);
    const [chartNumber, setChartNumber] = useState(1);
    const [hoveredIcon, setHoveredIcon] = useState(null);

    useEffect(() => {
        if (tsStart && tsEnd) {
            if (
                areAllFilterValuesNumberValues({
                    startMonth: tsStart.epoch,
                    endMonth: tsEnd.epoch,
                    zone: zoneValue,
                    market: marketValue,
                    team: teamValue,
                    rm: rmValue
                })
            ) {
                const checkedMarketValue =
                    allValues.market.value === marketValue ? null : marketValue;
                const checkedTeamValue =
                    allValues.team.value === teamValue ? null : teamValue;
                const checkedRmValue =
                    allValues.rm.value === rmValue ? null : rmValue;

                dispatch(
                    fetchTopRanks(
                        generateRequestParamsForLandingApi(
                            isTypeNumber(zoneValue) ? zoneValue : null,
                            checkedMarketValue,
                            checkedTeamValue,
                            checkedRmValue,
                            tsStart.epoch,
                            tsEnd.epoch
                        )
                    )
                );
                dispatch(
                    fetchTopAndEmergingSpikes(
                        generateRequestParamsForLandingApi(
                            isTypeNumber(zoneValue) ? zoneValue : null,
                            checkedMarketValue,
                            checkedTeamValue,
                            checkedRmValue,
                            tsStart.epoch,
                            tsEnd.epoch
                        )
                    )
                );
                dispatch(
                    fetchLandingPageStats(
                        generateRequestParamsForLandingApi(
                            null,
                            marketValue,
                            teamValue,
                            rmValue,
                            tsStart.epoch,
                            tsEnd.epoch
                        )
                    )
                );
            }
        }
    }, [marketValue, teamValue, rmValue, tsStart, tsEnd]);

    useEffect(() => {
        const chartContainerElement = document.querySelector(
            'div[data-title^=charts]'
        );
        setChartsContainerHeight(chartContainerElement.offsetHeight);
    });

    const cardsDataArray = [
        {
            titleOne: 'Current Risk',
            subTitleOne: '- By Total Risk Score',
            titleTwo: 'Current Risk',
            subTitleTwo: '- By Highest Spike',
            upperRightContentOne: generateUpperRightDateRangeContent({
                tsStart,
                tsEnd,
                nonSpikeStart,
                nonSpikeEnd,
                start,
                end,
                shouldShowSameWhenOneMonth: true
            }),
            upperRightContentTwo: generateUpperRightDateRangeContent({
                tsStart,
                tsEnd,
                nonSpikeStart,
                nonSpikeEnd,
                start,
                end,
                isSpike: true
            }),
            footerOne: 'Total Risk Score',
            footerTwo: ['Total Risk Score', '(Spike)'],
            groupOneData: topFiveRms,
            groupTwoData: topFiveRmsSpike,
            groupOneDataType: RMCardGroupDataType.TopFiveRms,
            groupTwoDataType: RMCardGroupDataType.TopFiveRmsSpike,
            groupOneTooltipType: RMCardGroupTooltipType.ScoreDecomposition,
            groupTwoTooltipType: RMCardGroupTooltipType.TrendAnalysis,
            isBottomGroup: false
        },
        {
            titleOne: 'Emerging Risk',
            titleTwo: 'Emerging Risk',
            subTitleOne: '- Red Threshold Breach Prediction',
            subTitleTwo: '- Amber Threshold Breach Prediction',
            upperRightContentOne: generateUpperRightDateRangeContent({
                tsStart,
                tsEnd,
                nonSpikeStart,
                nonSpikeEnd,
                start,
                end,
                isSpike: true
            }),
            upperRightContentTwo: generateUpperRightDateRangeContent({
                tsStart,
                tsEnd,
                nonSpikeStart,
                nonSpikeEnd,
                start,
                end,
                isSpike: true
            }),
            footerOne: 'Total Risk Score',
            footerTwo: 'Total Risk Score',
            groupOneData: topFiveEmergingMajorRms,
            groupTwoData: topFiveEmergingModerateRms,
            groupOneDataType: RMCardGroupDataType.TopFiveEmergingMajorRm,
            groupTwoDataType: RMCardGroupDataType.TopFiveEmergingModerateRm,
            groupOneTooltipType: RMCardGroupTooltipType.Default,
            groupTwoTooltipType: RMCardGroupTooltipType.Default,
            isBottomGroup: true
        }
    ];

    const onChartNodeMouseOver = (param) => {
        const { data, seriesName, dataIndex } = param;
        if (seriesName) {
            const dataToUse = seriesName.toLowerCase().includes('markets')
                ? topThreeMarkets
                : topFiveTeams;
            const dataToPassToTooltip = dataToUse.find(
                (tData) => tData.score === data
            );
            if (seriesName.toLowerCase().includes('markets')) {
                setChartNumber(1);
            } else {
                setChartNumber(2);
            }

            setBarValue(data);
            setTooltipData(dataToPassToTooltip);
            setSelectedDataIndex(dataIndex);
            setHighestScore(Math.max(...dataToUse.map((d) => d.score)));
            setShowTooltip(true);
        }
    };

    const onChartNodeMouseOut = () => {
        setShowTooltip(false);
    };

    const topThreeMarketsOnClick = (param) => {
        const market = marketList.find((r) => r.label === param.name);

        if (Object.keys(market).length > 0) {
            dispatch(
                updateFilter({
                    value: market.value,
                    label: market.label,
                    filterType: FilterType.MARKET
                })
            );

            const requestParamsObj = generateRequestParamsForFilterApi(
                tsStart ? tsStart.epoch : null,
                tsEnd ? tsEnd.epoch : null,
                zoneValue,
                market.value,
                null,
                null
            );
            const enrichedParamsObj = {
                requestParams: {
                    ...requestParamsObj.requestParams,
                    selectedFilterType: FilterType.MARKET,
                    label: market.label,
                    value: market.value
                }
            };

            dispatch(fetchFilteredDropdownValues(enrichedParamsObj));

            navigateTo('/', MARKET_ROUTE);
        }
    };

    const topThreeTeamsOnClick = (param) => {
        const team = teamList.find((r) => r.label === param.name);

        dispatch(
            updateFilter({
                value: team.value,
                label: team.label,
                filterType: FilterType.TEAM
            })
        );

        const requestParamsObj = generateRequestParamsForFilterApi(
            tsStart ? tsStart.epoch : null,
            tsEnd ? tsEnd.epoch : null,
            zoneValue,
            null,
            team.value,
            null
        );
        const enrichedParamsObj = {
            requestParams: {
                ...requestParamsObj.requestParams,
                selectedFilterType: FilterType.TEAM,
                label: team.label,
                value: team.value
            }
        };

        dispatch(fetchFilteredDropdownValues(enrichedParamsObj));

        navigateTo('/', TEAM_ROUTE);
    };

    const onTopThreeMarketEvents = {
        mouseover: onChartNodeMouseOver,
        mouseout: onChartNodeMouseOut,
        click: topThreeMarketsOnClick,
        globalout: onChartNodeMouseOut
    };

    const onTopThreeTeamsEvents = {
        mouseover: onChartNodeMouseOver,
        mouseout: onChartNodeMouseOut,
        click: topThreeTeamsOnClick,
        globalout: onChartNodeMouseOut
    };

    const calculateTopValue = () => {
        const middleOfContainerHeight = chartsContainerHeight / 2;
        if (chartNumber === 1)
            return calculateTopValueForLandingGraphTooltip(
                topThreeMarkets.length,
                middleOfContainerHeight,
                selectedDataIndex
            );
        else
            return calculateTopValueForLandingGraphTooltip(
                topFiveTeams.length,
                middleOfContainerHeight,
                selectedDataIndex
            );
    };

    const calculateLeftValue = () => {
        if (chartNumber === 1) {
            const baseCalculation =
                42 +
                (barValue / highestScore) * 20 +
                (highestScore - barValue) / 200;
            return barValue === highestScore
                ? baseCalculation
                : baseCalculation +
                      (barValue / 100) * 10 -
                      (highestScore - barValue) * 0.5;
        } else {
            const baseCalculation =
                140 +
                (barValue / highestScore) * 20 +
                (highestScore - barValue) / 200;
            return barValue === highestScore
                ? baseCalculation
                : baseCalculation +
                      (barValue / 100) * 10 -
                      (highestScore - barValue) * 0.5;
        }
    };

    const memoizedTopThreeMarkets = useMemo(() => {
        return (
            <ReactECharts
                option={landingTopThreeMarketOptions(
                    topThreeMarkets &&
                        orderBy(topThreeMarkets, ['score'], ['asc'])
                )}
                style={{ width: '100%', height: '100%' }}
                onEvents={onTopThreeMarketEvents}
            ></ReactECharts>
        );
    }, [topThreeMarkets]);

    const memoizedTopThreeTeams = useMemo(() => {
        return (
            <ReactECharts
                option={landingTopThreeTeamsOptions(topFiveTeams)}
                style={{ width: '100%', height: '100%' }}
                onEvents={onTopThreeTeamsEvents}
                notMerge={true}
            ></ReactECharts>
        );
    }, [topFiveTeams]);

    const isTooltipContainsFiveBar =
        tooltipData.s1Score > 0 &&
        tooltipData.s2Score > 0 &&
        tooltipData.s3Score > 0 &&
        tooltipData.s4Score > 0 &&
        tooltipData.correlationScore > 0;

    return (
        <ContentContainer>
            <div className={styles.chartsContainer} data-title={'charts'}>
                <div className={styles.leftChartsContainer}>
                    <ChartWrapper
                        data-title={'charts'}
                        comboClass={styles.rightMargin}
                        width={'100%'}
                        height={'300px'}
                    >
                        {memoizedTopThreeMarkets}
                    </ChartWrapper>
                    <div className={styles.iconContainerLeft}>
                        <img
                            src={
                                hoveredIcon === 'market'
                                    ? darkInfoIcon
                                    : infoIcon
                            }
                            alt={''}
                            onMouseOver={() => setHoveredIcon('market')}
                            onMouseOut={() => setHoveredIcon(null)}
                        />
                        <div
                            className={styles.container}
                            style={{
                                visibility:
                                    hoveredIcon === 'market'
                                        ? 'visible'
                                        : 'hidden'
                            }}
                        >
                            <p>
                                Σ of RMs score in the Market / Total # of RMs in
                                the Market
                            </p>
                        </div>
                    </div>
                    {showTooltip && (
                        <ChartWrapper
                            data-title={'charts'}
                            comboClass={styles.absolutePosition}
                            style={{
                                top: `${calculateTopValue()}px`,
                                left: `${calculateLeftValue()}%`
                            }}
                            width={'320px'}
                            height={'auto'}
                        >
                            <ReactECharts
                                option={scoreDecompositionChartOptions(
                                    tooltipData
                                )}
                                style={
                                    isTooltipContainsFiveBar
                                        ? { width: '300px', height: '330px' }
                                        : { width: '300px' }
                                }
                            ></ReactECharts>
                        </ChartWrapper>
                    )}
                </div>

                <div className={styles.rightChartsContainer}>
                    <ChartWrapper width={'100%'} height={'300px'}>
                        {memoizedTopThreeTeams}
                    </ChartWrapper>
                    <div className={styles.iconContainerRight}>
                        <img
                            src={
                                hoveredIcon === 'team' ? darkInfoIcon : infoIcon
                            }
                            alt={''}
                            onMouseOver={() => setHoveredIcon('team')}
                            onMouseOut={() => setHoveredIcon(null)}
                        />
                        <div
                            className={styles.container}
                            style={{
                                visibility:
                                    hoveredIcon === 'team'
                                        ? 'visible'
                                        : 'hidden'
                            }}
                        >
                            <p>
                                Σ of RMs score in the Team / Total # of RMs in
                                the Team
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <RmCardsGroupSeries
                seriesTitle={'Top 5 RMs'}
                minorThreshold={minorThreshold}
                majorThreshold={majorThreshold}
                cardsDataArray={cardsDataArray}
                navigateTo={navigateTo}
            />
        </ContentContainer>
    );
};

Landing.propTypes = {
    navigateTo: PropTypes.func
};
export default Landing;
